import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../state/language/LanguageProvider';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { partnerService } from '../../services/partner';
import api from '@/services/api';
import { toast } from 'react-hot-toast';
import { EyeIcon, UserPlusIcon, TrashIcon, CogIcon } from '@heroicons/react/24/outline';

interface PartnerUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  tenant_name: string;
  tenant_id: string;
  status: string;
  partner_status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'CANCELLED' | 'NO_INVITATION';
  invitation_id?: string;
  created_at: string;
  last_login?: string;
}

// Remove unused interface

const PartnerManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedPartner, setSelectedPartner] = useState<PartnerUser | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    message: ''
  });

  const tableRef = useRef<any>(null);

  // Load partner invitations
  const loadInvitations = async () => {
    try {
      await partnerService.getPendingInvitations();
      // Store invitations data if needed for future use
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, []);

  // Handle view partner details
  const handleViewPartner = (partner: PartnerUser) => {
    setSelectedPartner(partner);
    setShowViewModal(true);
  };

  // Handle invite partner
  const handleInvitePartner = () => {
    setShowInviteModal(true);
  };

  // Handle send invitation
  const handleSendInvitation = async () => {
    try {
      setIsLoading(true);
      await partnerService.sendInvitation(inviteForm);
      toast.success(t('partner.invitationSentSuccessfully'));
      setShowInviteModal(false);
      setInviteForm({ email: '', first_name: '', last_name: '', message: '' });
      loadInvitations();
      if (tableRef.current) {
        tableRef.current.loadData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('partner.failedToSendInvitation'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle remove partner
  const handleRemovePartner = async (partner: PartnerUser) => {
    setSelectedPartner(partner);
    setShowDeleteConfirm(true);
  };

  // Handle confirm remove
  const handleConfirmRemove = async () => {
    if (!selectedPartner?.invitation_id) {
      toast.error(t('partner.noInvitationFound'));
      return;
    }
    
    try {
      setIsLoading(true);
      // Update invitation status to CANCELLED
      await api.put(`/partner/management/${selectedPartner.invitation_id}/status`, { 
        status: 'CANCELLED' 
      });
      toast.success(t('partner.partnerRemovedSuccessfully'));
      setShowDeleteConfirm(false);
      setSelectedPartner(null);
      if (tableRef.current) {
        tableRef.current.loadData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('partner.failedToRemovePartner'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle assign partner (placeholder for future functionality)
  const handleAssignPartner = (partner: PartnerUser) => {
    setSelectedPartner(partner);
    setShowAssignModal(true);
  };

  // Close modals
  const closeViewModal = () => setShowViewModal(false);
  const closeInviteModal = () => setShowInviteModal(false);
  const closeAssignModal = () => setShowAssignModal(false);
  const closeDeleteConfirm = () => setShowDeleteConfirm(false);

    // Partner entity configuration for CRUD table
  const partnerEntity = {
    name: 'partner',
    displayName: 'Partner',
    displayNameSingular: 'Partner',
    apiEndpoint: '/partner/management',
    idField: 'id',
    nameField: 'email',
    
    fields: [
      { key: 'email', label: t('partnerManagement.fields.email'), type: 'text', required: true },
      { key: 'first_name', label: t('partnerManagement.fields.firstName'), type: 'text' },
      { key: 'last_name', label: t('partnerManagement.fields.lastName'), type: 'text' },
      { key: 'tenant_name', label: t('partnerManagement.fields.company'), type: 'text' },
      { key: 'partner_status', label: t('partnerManagement.fields.status'), type: 'select', options: [
        { value: 'PENDING', label: t('partnerManagement.status.pending') },
        { value: 'ACCEPTED', label: t('partnerManagement.status.accepted') },
        { value: 'EXPIRED', label: t('partnerManagement.status.expired') },
        { value: 'CANCELLED', label: t('partnerManagement.status.cancelled') },
        { value: 'NO_INVITATION', label: t('partnerManagement.status.noInvitation') }
      ]},
      { key: 'created_at', label: t('partnerManagement.invitedDate'), type: 'date' },
      { key: 'last_login', label: t('partnerManagement.lastLogin'), type: 'date' }
    ],

    tableColumns: [
      {
        key: 'email',
        label: t('partnerManagement.fields.email'),
        sortable: true,
        width: '25%'
      },
      {
        key: 'first_name',
        label: t('partnerManagement.fields.firstName'),
        sortable: true,
        width: '15%'
      },
      {
        key: 'last_name',
        label: t('partnerManagement.fields.lastName'),
        sortable: true,
        width: '15%'
      },
      {
        key: 'tenant_name',
        label: t('partnerManagement.fields.company'),
        sortable: true,
        width: '20%'
      },
      {
        key: 'partner_status',
        label: t('partnerManagement.fields.status'),
        sortable: true,
        width: '15%',
        render: (value: string) => {
          const getStatusColor = (status: string) => {
            switch (status) {
              case 'PENDING': return 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]';
              case 'ACCEPTED': return 'bg-[var(--color-success)]/10 text-[var(--color-success)]';
              case 'EXPIRED': return 'bg-[var(--color-error)]/10 text-[var(--color-error)]';
              case 'CANCELLED': return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]';
              case 'NO_INVITATION': return 'bg-[var(--color-info)]/10 text-[var(--color-info)]';
              default: return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]';
            }
          };
          return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(value)}`}>
              {value}
            </span>
          );
        }
      },
      {
        key: 'created_at',
        label: t('partnerManagement.invitedDate'),
        sortable: true,
        width: '10%',
        render: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A'
      }
    ],

    searchFields: ['email', 'first_name', 'last_name', 'tenant_name'],
    
    defaultSort: {
      field: 'email',
      direction: 'asc'
    }
  };

  // Custom actions for the CRUD table
  const customActions = [
    {
      label: t('partnerManagement.actions.view'),
      icon: EyeIcon,
      onClick: handleViewPartner,
      variant: 'secondary' as const
    },
    {
      label: t('partnerManagement.actions.assign'),
      icon: CogIcon,
      onClick: handleAssignPartner,
      variant: 'secondary' as const
    },
    {
      label: t('partnerManagement.actions.remove'),
      icon: TrashIcon,
      onClick: handleRemovePartner,
      variant: 'danger' as const
    }
  ];

  // Custom CRUD service for partners
  const crudService = {
    getAll: async (params?: { search?: string; sort?: string; page?: number; limit?: number }) => {
      const response = await api.get('/partner/management', { params });
      return response.data;
    },
    getById: async (id: string) => {
      const response = await api.get(`/partner/management/${id}`);
      return response.data;
    },
    create: async (data: any) => {
      const response = await api.post('/partner/management', data);
      return response.data;
    },
    update: async (id: string, data: any) => {
      const response = await api.put(`/partner/management/${id}`, data);
      return response.data;
    },
    delete: async (id: string) => {
      await api.delete(`/partner/management/${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="mb-2">
          <h1 className="text-xl font-bold text-[var(--color-surface-text)] mb-1">{t('partnerManagement.title')}</h1>
          <p className="text-xs text-[var(--color-secondary-text)]">
            {t('partnerManagement.description')}
          </p>
        </div>

        <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)]">
          <div className="p-2">
            <div className="flex justify-between items-center mb-2">
<h2 className="text-sm font-semibold text-[var(--color-surface-text)]">{t('partnerManagement.partnerOrganizations')}</h2>
            <Button onClick={handleInvitePartner} className="flex items-center space-x-2">
              <UserPlusIcon className="w-4 h-4" />
              <span>{t('partnerManagement.invitePartner')}</span>
            </Button>
          </div>

                     <FlexibleCRUDTable
             entity={partnerEntity}
             service={crudService}
             searchPlaceholder={t('partnerManagement.searchPartners')}
             customActions={customActions}
            />
          </div>
        </div>
      </div>

      {/* View Partner Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={closeViewModal}
title={`${t('partnerManagement.partnerDetails')} - ${selectedPartner?.first_name} ${selectedPartner?.last_name}`}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.fields.email')}</label>
              <p className="mt-1 text-sm text-[var(--color-surface-text)]">{selectedPartner?.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.fields.company')}</label>
              <p className="mt-1 text-sm text-[var(--color-surface-text)]">{selectedPartner?.tenant_name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.fields.status')}</label>
              <p className="mt-1 text-sm text-[var(--color-surface-text)]">{selectedPartner?.partner_status}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.invitedDate')}</label>
              <p className="mt-1 text-sm text-[var(--color-surface-text)]">
                {selectedPartner?.created_at ? new Date(selectedPartner.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.lastLogin')}</label>
              <p className="mt-1 text-sm text-[var(--color-surface-text)]">
                {selectedPartner?.last_login ? new Date(selectedPartner.last_login).toLocaleDateString() : t('partnerManagement.never')}
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={closeViewModal}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Invite Partner Modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={closeInviteModal}
        title={t('partnerManagement.invitePartnerModal.title')}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.invitePartnerModal.emailAddress')}</label>
            <input
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
              className="mt-1 block w-full border border-[var(--color-border)] rounded-md px-3 py-2 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
              placeholder={t('partnerManagement.invitePartnerModal.emailPlaceholder')}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.invitePartnerModal.firstName')}</label>
              <input
                type="text"
                value={inviteForm.first_name}
                onChange={(e) => setInviteForm(prev => ({ ...prev, first_name: e.target.value }))}
                className="mt-1 block w-full border border-[var(--color-border)] rounded-md px-3 py-2 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                placeholder={t('partnerManagement.invitePartnerModal.firstNamePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.invitePartnerModal.lastName')}</label>
              <input
                type="text"
                value={inviteForm.last_name}
                onChange={(e) => setInviteForm(prev => ({ ...prev, last_name: e.target.value }))}
                className="mt-1 block w-full border border-[var(--color-border)] rounded-md px-3 py-2 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                placeholder={t('partnerManagement.invitePartnerModal.lastNamePlaceholder')}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.invitePartnerModal.message')}</label>
            <textarea
              value={inviteForm.message}
              onChange={(e) => setInviteForm(prev => ({ ...prev, message: e.target.value }))}
              rows={3}
              className="mt-1 block w-full border border-[var(--color-border)] rounded-md px-3 py-2 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
              placeholder={t('partnerManagement.invitePartnerModal.messagePlaceholder')}
            />
          </div>
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={closeInviteModal}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSendInvitation} disabled={isLoading || !inviteForm.email}>
              {isLoading ? t('partnerManagement.invitePartnerModal.sending') : t('partnerManagement.invitePartnerModal.sendInvitation')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assign Partner Modal (Placeholder for future functionality) */}
      <Modal
        isOpen={showAssignModal}
        onClose={closeAssignModal}
title={`${t('partnerManagement.assignPartnerModal.title')} - ${selectedPartner?.first_name} ${selectedPartner?.last_name}`}
        size="lg"
      >
        <div className="space-y-4">
          <div className="text-center py-8">
            <CogIcon className="mx-auto h-12 w-12 text-[var(--color-secondary-text)] opacity-75" />
            <h3 className="mt-2 text-sm font-medium text-[var(--color-surface-text)]">{t('partnerManagement.assignPartnerModal.partnerAssignment')}</h3>
            <p className="mt-1 text-sm text-[var(--color-secondary-text)]">
              {t('partnerManagement.assignPartnerModal.description')}
            </p>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={closeAssignModal}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={closeDeleteConfirm}
        title={t('partnerManagement.removePartnerModal.title')}
      >
        <div className="space-y-4">
                      <p>
              {t('partnerManagement.removePartnerModal.confirmRemove', {
                firstName: selectedPartner?.first_name || '',
                lastName: selectedPartner?.last_name || ''
              })}
            </p>
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={closeDeleteConfirm}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRemove}
              disabled={isLoading}
            >
              {isLoading ? t('partnerManagement.removePartnerModal.removing') : t('partnerManagement.removePartnerModal.removePartner')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PartnerManagementPage;
